import React, { FunctionComponent, useEffect, useState } from "react";
import MaterialTable from "@material-table/core";
import { Store } from "../../../../models/Store";
import { ProblemDetails } from "../../../../models/Http";
import { Alert, AlertTitle } from "@material-ui/lab";
import { useDispatch, useSelector } from "react-redux";
import { AppState } from "../../../../store";
import { thunkFetchStoreDistributors } from "../../../../store/stores/actions";
import {
  StoreDistributor,
  StoreDistributorWriteModel,
  toStoreDistributorWriteModel
} from "../../../../models/StoreDistributor";
import { ConfirmDialog } from "../../../../components/dialogs/ConfirmDialog";
import {
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Tooltip,
  useMediaQuery
} from "@material-ui/core";
import { v4 as uuidv4 } from "uuid";
import { WeekDayMultiSelect } from "../../../../components/selects/WeekDayMultiSelect";
import { KeyboardTimePicker } from "@material-ui/pickers";
import useStyles from "./styles";
import moment from "moment";
import * as StoreDistributorApi from "../../../../api/storeDistributorApi";
import { isOk } from "../../../../result";
import { DistributorSelect } from "../../../../components/selects/DistributorSelect";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import { StoreDistributorsState } from "../../../../store/stores/types";
import { useTheme } from "@material-ui/core/styles";
import { useI18n } from "../../../../hooks/useI18n";

enum snackBarMessage {
  ADD = 1,
  EDIT,
  DELETE
}

interface StoreDistributorsProps {
  store: Store;
}

const initialStoreDistributorValidationErrors = () => ({
  distributorId: false,
  orderWeekDays: false,
  orderCutOffTime: false,
  deliveryWeekDays: false,
  deliveryTime: false,
  externalCustomerId: false,
  externalPassword: false
});

export const StoreDistributors: FunctionComponent<StoreDistributorsProps> = ({
  store
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useI18n();

  const mobile = useMediaQuery(theme.breakpoints.down("xs"));
  const [showSnackbar, setShowSnackbar] = useState(0);
  const [reloadDistributors, setReloadDistributors] = useState<boolean>(false);
  const [viewDistributor, setViewDistributor] = useState({
    visible: false,
    showPassword: false,
    current: {} as StoreDistributor
  });
  const [deleteDistributor, setDeleteDistributor] = useState({
    visible: false,
    current: {} as StoreDistributor,
    problem: {} as ProblemDetails
  });
  const [upsertDistributor, setUpsertDistributor] = useState({
    newDistributor: false,
    visible: false,
    current: {} as StoreDistributor,
    write: {} as StoreDistributorWriteModel,
    errors: initialStoreDistributorValidationErrors(),
    problem: {} as ProblemDetails
  });

  const storeDistributorsState = useSelector<AppState, StoreDistributorsState>(
    s => s.storeDistributors
  );

  let { isLoading, storeDistributors } = storeDistributorsState;

  useEffect(() => {
    if (store || reloadDistributors)
      dispatch(thunkFetchStoreDistributors(store.id));
    setReloadDistributors(false);
  }, [store, reloadDistributors]);

  const handleCloseViewDialog = (): boolean => {
    setViewDistributor({
      visible: false,
      showPassword: false,
      current: {} as StoreDistributor
    });
    return true;
  };

  const handleCloseDeleteDialog = (): boolean => {
    setDeleteDistributor({
      visible: false,
      current: {} as StoreDistributor,
      problem: {} as ProblemDetails
    });
    return true;
  };

  const handleCloseEditDialog = (): boolean => {
    setUpsertDistributor({
      ...upsertDistributor,
      newDistributor: false,
      visible: false,
      errors: initialStoreDistributorValidationErrors(),
      problem: {} as ProblemDetails
    });
    return true;
  };

  const handleConfirmUpsertDialog = async (): Promise<boolean> => {
    if (!isValidForm()) return false;

    let result;
    if (upsertDistributor.newDistributor) {
      result = await StoreDistributorApi.add(store.id, upsertDistributor.write);
    } else {
      result = await StoreDistributorApi.update(
        store.id,
        upsertDistributor.write
      );
    }

    if (!isOk(result)) {
      setUpsertDistributor({ ...upsertDistributor, problem: result.details });
      return false;
    } else {
      setShowSnackbar(
        upsertDistributor.newDistributor
          ? snackBarMessage.ADD
          : snackBarMessage.EDIT
      );
      setReloadDistributors(true);
    }
    return true;
  };

  const isValidForm = (): boolean => {
    let errors = upsertDistributor.errors;

    errors.orderWeekDays =
      upsertDistributor.write.orderWeekDays === undefined ||
      upsertDistributor.write.orderWeekDays.length === 0;
    errors.orderCutOffTime =
      upsertDistributor.write.orderCutOffTime === undefined ||
      upsertDistributor.write.orderCutOffTime.length === 0;
    errors.deliveryWeekDays =
      upsertDistributor.write.deliveryWeekDays === undefined ||
      upsertDistributor.write.deliveryWeekDays.length === 0;
    errors.deliveryTime =
      upsertDistributor.write.deliveryTime === undefined ||
      upsertDistributor.write.deliveryTime.length === 0;

    if (
      errors.orderWeekDays ||
      errors.orderCutOffTime ||
      errors.deliveryTime ||
      errors.deliveryWeekDays
    ) {
      setUpsertDistributor({ ...upsertDistributor, errors });
      return false;
    }

    return true;
  };

  const renderPassword = (item: any) =>
    !item.current.externalPassword
      ? "..."
      : item.showPassword
      ? item.current.externalPassword
      : "************";

  const getDateWithTime = (time?: string): any => {
    if (!time) return null;

    if (time.length > 5) time = time.substr(0, 5);

    let str = `${moment().format("YYYY-MM-DD")} ${time}`;
    return new Date(str);
  };

  const renderWeekInterval = (weekOffset?: number): string => {
    switch (weekOffset) {
      case 1:
        return t("common.messages.everyWeek");

      case 2:
        return t("common.messages.everyTwoWeeks");

      case 3:
        return t("common.messages.everyThreeWeeks");

      case 4:
        return t("common.messages.everyFourWeeks");

      default:
        return t("common.messages.everyWeek");
    }
  };

  const weekOffsets = [1, 2, 3, 4];

  return (
    <>
      <MaterialTable
        title=""
        columns={[
          {
            title: "Distributor",
            field: "distributorName",
            cellStyle: { padding: mobile ? "8px" : "" }
          },
          {
            title: "Order day(s)",
            field: "orderWeekDays",
            cellStyle: { padding: mobile ? "8px" : "" },
            render: rowData => rowData.orderWeekDays ?? "..."
          },
          {
            title: "Delivery day(s)",
            field: "deliveryWeekDays",
            cellStyle: { padding: mobile ? "8px" : "" },
            render: rowData => rowData.deliveryWeekDays ?? "..."
          }
        ]}
        data={
          storeDistributors && storeDistributors.length
            ? storeDistributors.map(item => ({
                distributorId: item.distributor.id,
                distributorName: item.distributor.name,
                orderWeekDays: item.orderWeekDays,
                deliveryWeekDays: item.deliveryWeekDays
              }))
            : []
        }
        isLoading={isLoading}
        options={{
          actionsColumnIndex: -1,
          actionsCellStyle: { whiteSpace: "nowrap" },
          headerStyle: {
            padding: mobile ? "8px" : ""
          },
          pageSize: 10,
          pageSizeOptions: [10, 20, 50, 100]
        }}
        onRowClick={(event, row) => {
          if (isLoading) return;

          let current =
            row &&
            storeDistributors.find(x => x.distributor.id === row.distributorId);

          if (!current) return;

          return setViewDistributor({
            visible: true,
            current: current,
            showPassword: false
          });
        }}
        actions={[
          {
            icon: "add_circle",
            iconProps: {
              style: { color: theme.palette.primary.main, fontSize: "2rem" }
            },
            tooltip: "Add new distributor",
            isFreeAction: true,
            onClick: () =>
              setUpsertDistributor({
                ...upsertDistributor,
                newDistributor: true,
                visible: true,
                write: { weekInterval: 1 }
              })
          },
          {
            icon: "edit",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.actions.editDistributor"),
            onClick: (event, rowData: any) => {
              let item = storeDistributors.find(
                x => x.distributor.id === rowData.distributorId
              );

              if (!item) return;

              return setUpsertDistributor({
                ...upsertDistributor,
                newDistributor: false,
                visible: true,
                current: item,
                write: toStoreDistributorWriteModel(item)
              });
            }
          },
          {
            icon: "delete",
            iconProps: { style: { color: "rgba(0, 0, 0, 0.54)" } },
            tooltip: t("common.actions.removeDistributor"),
            onClick: (event, rowData: any) => {
              let item = storeDistributors.find(
                x => x.distributor.id === rowData.distributorId
              );

              if (!item) return;

              return setDeleteDistributor({
                ...deleteDistributor,
                visible: true,
                current: item
              });
            }
          }
        ]}
        localization={{
          header: {
            actions: ""
          }
        }}
      />
      <ConfirmDialog
        visible={viewDistributor.visible}
        titleText={viewDistributor.current?.distributor?.name}
        confirmText={"Ok"}
        showCancelButton={false}
        onCancel={handleCloseViewDialog}
        onConfirm={handleCloseViewDialog}
      >
        <List style={{ minWidth: "300px" }}>
          <ListItem key={uuidv4()}>
            <ListItemText
              primary={t("common.formFields.orderDays")}
              secondary={viewDistributor.current.orderWeekDays}
            />
          </ListItem>
          <ListItem key={uuidv4()}>
            <ListItemText
              primary={t("common.formFields.orderCutOffTime")}
              secondary={viewDistributor.current.orderCutOffTime?.substr(0, 5)}
            />
          </ListItem>
          <ListItem key={uuidv4()}>
            <ListItemText
              primary={t("common.formFields.orderNotes")}
              secondary={viewDistributor.current.orderNotes ?? "..."}
            />
          </ListItem>
          <ListItem key={uuidv4()}>
            <ListItemText
              primary={t("common.formFields.orderCutOffTime")}
              secondary={viewDistributor.current.orderCutOffTime?.substr(0, 5)}
            />
          </ListItem>
          <ListItem key={uuidv4()}>
            <ListItemText
              primary={t("common.formFields.deliveryTime")}
              secondary={viewDistributor.current.deliveryTime?.substr(0, 5)}
            />
          </ListItem>
          <ListItem key={uuidv4()}>
            <ListItemText
              primary={t("common.formFields.weekInterval")}
              secondary={renderWeekInterval(
                viewDistributor.current.weekInterval
              )}
            />
          </ListItem>
          <ListItem key={uuidv4()}>
            <ListItemText
              primary={t("common.formFields.externalCustomerId")}
              secondary={viewDistributor.current.externalCustomerId ?? "..."}
            />
          </ListItem>
          <ListItem key={uuidv4()}>
            <ListItemText
              primary={t("common.formFields.externalPassword")}
              secondary={renderPassword(viewDistributor)}
            />
            {viewDistributor.current.externalPassword ? (
              <Button
                onClick={() =>
                  setViewDistributor({
                    ...viewDistributor,
                    showPassword: !viewDistributor.showPassword
                  })
                }
              >
                Show
              </Button>
            ) : null}
          </ListItem>
        </List>
      </ConfirmDialog>
      <ConfirmDialog
        visible={upsertDistributor.visible}
        titleText={
          upsertDistributor.newDistributor
            ? "Adding"
            : `Editing ${upsertDistributor.current?.distributor?.name}`
        }
        confirmText={"Save"}
        onCancel={handleCloseEditDialog}
        onConfirm={handleConfirmUpsertDialog}
      >
        <div style={{ minWidth: "300px" }}>
          {upsertDistributor.newDistributor ? (
            <DistributorSelect
              error={upsertDistributor.errors.distributorId}
              className={classes.field}
              value={upsertDistributor.write.distributorId}
              onSelected={distributor =>
                setUpsertDistributor({
                  ...upsertDistributor,
                  write: {
                    ...upsertDistributor.write,
                    distributorId: distributor?.id
                  }
                })
              }
            />
          ) : null}
          <WeekDayMultiSelect
            required
            error={upsertDistributor.errors.orderWeekDays}
            className={classes.field}
            label={t("common.formFields.orderDays")}
            value={
              upsertDistributor.write.orderWeekDays
                ? upsertDistributor.write.orderWeekDays.split(", ")
                : []
            }
            onSelected={weekDays =>
              setUpsertDistributor({
                ...upsertDistributor,
                write: {
                  ...upsertDistributor.write,
                  orderWeekDays: weekDays.join(", ")
                }
              })
            }
          />
          <KeyboardTimePicker
            required
            error={upsertDistributor.errors.orderCutOffTime}
            className={classes.field}
            id="order-time-picker"
            label={t("common.formFields.orderCutOffTime")}
            value={getDateWithTime(upsertDistributor.write.orderCutOffTime)}
            ampm={false}
            onChange={d =>
              setUpsertDistributor({
                ...upsertDistributor,
                write: {
                  ...upsertDistributor.write,
                  orderCutOffTime: d ? moment(d).format("HH:mm") : undefined
                }
              })
            }
            KeyboardButtonProps={{
              "aria-label": "change order cut-off time"
            }}
          />
          <TextField
            className={classes.field}
            label={t("common.formFields.orderNotes")}
            value={upsertDistributor.write.orderNotes}
            onChange={e =>
              setUpsertDistributor({
                ...upsertDistributor,
                write: {
                  ...upsertDistributor.write,
                  orderNotes: e.target.value as string
                }
              })
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip
                    title={t(
                      "common.tooltip.notesRegardingMinimumOrderingQuantity"
                    )}
                  >
                    <HelpOutlineIcon style={{ fontSize: "0.9rem" }} />
                  </Tooltip>
                </InputAdornment>
              )
            }}
          />
          <WeekDayMultiSelect
            required
            error={upsertDistributor.errors.deliveryWeekDays}
            className={classes.field}
            label={t("common.formFields.deliveryDays")}
            value={
              upsertDistributor.write.deliveryWeekDays
                ? upsertDistributor.write.deliveryWeekDays.split(", ")
                : []
            }
            onSelected={weekDays =>
              setUpsertDistributor({
                ...upsertDistributor,
                write: {
                  ...upsertDistributor.write,
                  deliveryWeekDays: weekDays.join(", ")
                }
              })
            }
          />
          <KeyboardTimePicker
            required
            error={upsertDistributor.errors.deliveryTime}
            className={classes.field}
            id="delivery-time-picker"
            label={t("common.formFields.deliveryTime")}
            value={getDateWithTime(upsertDistributor.write.deliveryTime)}
            ampm={false}
            onChange={d =>
              setUpsertDistributor({
                ...upsertDistributor,
                write: {
                  ...upsertDistributor.write,
                  deliveryTime: d ? moment(d).format("HH:mm") : undefined
                }
              })
            }
            KeyboardButtonProps={{
              "aria-label": "change order cut-off time"
            }}
          />
          <FormControl required variant={"standard"} className={classes.field}>
            <InputLabel id={`week-interval-label`}>
              {t("common.formFields.weekInterval")}
            </InputLabel>
            <Select
              value={upsertDistributor.write.weekInterval}
              onChange={e =>
                setUpsertDistributor({
                  ...upsertDistributor,
                  write: {
                    ...upsertDistributor.write,
                    weekInterval:
                      e.target.value && Number(e.target.value) > 0
                        ? Number(e.target.value)
                        : 1
                  }
                })
              }
              labelId={`week-interval-label`}
              id={"week-interval"}
              autoWidth={true}
            >
              {weekOffsets.map(i => (
                <MenuItem key={`week-interval-${i}`} value={i}>
                  <ListItemText primary={renderWeekInterval(i)} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            className={classes.field}
            label={t("common.formFields.externalCustomerId")}
            value={upsertDistributor.write.externalCustomerId}
            onChange={e =>
              setUpsertDistributor({
                ...upsertDistributor,
                write: {
                  ...upsertDistributor.write,
                  externalCustomerId: e.target.value as string
                }
              })
            }
          />
          <TextField
            className={classes.field}
            label={t("common.formFields.externalPassword")}
            type={"password"}
            value={upsertDistributor.write.externalPassword}
            onChange={e =>
              setUpsertDistributor({
                ...upsertDistributor,
                write: {
                  ...upsertDistributor.write,
                  externalPassword: e.target.value as string
                }
              })
            }
          />
        </div>

        {upsertDistributor.problem.status && (
          <Alert severity="error">
            <AlertTitle>
              {upsertDistributor.problem.status},{" "}
              {upsertDistributor.problem.title}
            </AlertTitle>
            {upsertDistributor.problem.detail}{" "}
            <strong>{upsertDistributor.problem.traceId}</strong>
          </Alert>
        )}
      </ConfirmDialog>
      <ConfirmDialog
        visible={deleteDistributor.visible}
        titleText={"Are you sure?"}
        confirmText={"REMOVE"}
        onCancel={handleCloseDeleteDialog}
        onConfirm={async () => {
          const result = await StoreDistributorApi.remove(
            store.id,
            deleteDistributor.current.distributor.id
          );
          if (!isOk(result)) {
            setDeleteDistributor({
              ...deleteDistributor,
              problem: result.details
            });
            return false;
          } else {
            setShowSnackbar(snackBarMessage.DELETE);
            setReloadDistributors(true);
          }

          return true;
        }}
      >
        <div>
          This will remove the distributor{" "}
          <b>{deleteDistributor.current?.distributor?.name}</b> and its
          information from the store. Press <b>REMOVE</b> to continue.
        </div>
        {deleteDistributor.problem.status && (
          <Alert severity="error">
            <AlertTitle>
              {deleteDistributor.problem.status},{" "}
              {deleteDistributor.problem.title}
            </AlertTitle>
            {deleteDistributor.problem.detail}{" "}
            <strong>{deleteDistributor.problem.traceId}</strong>
          </Alert>
        )}
      </ConfirmDialog>
      )
      <Snackbar
        open={showSnackbar > 0}
        autoHideDuration={3000}
        onClose={() => {
          handleCloseDeleteDialog();
          handleCloseEditDialog();
          setShowSnackbar(0);
        }}
      >
        {showSnackbar === snackBarMessage.ADD ? (
          <Alert severity="success">
            {t("common.messages.distributorAdded")}
          </Alert>
        ) : showSnackbar === snackBarMessage.DELETE ? (
          <Alert severity="success">
            {t("common.messages.distributorRemoved")}
          </Alert>
        ) : (
          <Alert severity="success">
            {t("common.messages.distributorUpdated")}
          </Alert>
        )}
      </Snackbar>
    </>
  );
};
